import api from "./api";

// ========================================
// ADMIN MANAGEMENT
// ========================================

const getAdmin = async (id) => {
  const response = await api.get(`/admins/${id}`);
  return response.data;
};

const getMyDetails = async () => {
  const response = await api.get("/admins/me");
  return response.data;
};

const createAdmin = async (adminData) => {
  const response = await api.post("/admins/create", adminData);
  return response.data;
};

const deleteAdmin = async (id) => {
  const response = await api.delete(`/admins/delete/${id}`);
  return response.data;
};

const updateAdminPassword = async (passwordData) => {
  const response = await api.post("/admins/update-password", passwordData);
  return response.data;
};

const resetPassword = async (passwordData) => {
  const response = await api.post("/admins/reset-password", passwordData);
  return response.data;
};

const requestPasswordReset = async (data) => {
  const response = await api.post("/admins/request-password-reset", data);
  return response.data;
};

const fetchAdmins = async (filterParams) => {
  const response = await api.get("/admins", { params: filterParams });
  return response.data;
};

const toggleBlockAdmin = async (adminCode, block) => {
  const response = await api.patch(`/admins/${adminCode}/toggle-block`, {
    block,
  });
  return response.data;
};

const getForgotPasswordRequests = async () => {
  const response = await api.get("/admins/forgot-password-requests");
  return response.data;
};

const completeInitialSetup = async (setupData) => {
  const response = await api.post("/admins/complete-setup", setupData);
  return response.data;
};

const updateAdminBranchAccess = async (adminId, branchData) => {
  const response = await api.put(
    `/admins/${adminId}/branch-access`,
    branchData
  );
  return response.data;
};

// ========================================
// STATUS MANAGEMENT
// ========================================

const getAdminStatus = async (adminCode) => {
  const response = await api.get(`/admin-availability/status/${adminCode}`);
  return response.data;
};

const updateMyStatus = async (statusData) => {
  const response = await api.put("/admin-availability/status", statusData);
  return response.data;
};

const getAvailableAdmins = async (params) => {
  // params: { branchCode, dateTime, appointmentType }
  const response = await api.get("/admin-availability/available", { params });
  return response.data;
};

// ========================================
// AVAILABILITY MANAGEMENT
// ========================================

const getMyAvailability = async () => {
  const response = await api.get("/admin-availability/my-availability");
  return response.data;
};

const updateWorkingHours = async (workingHoursData) => {
  const response = await api.put(
    "/admin-availability/working-hours",
    workingHoursData
  );
  return response.data;
};

const addTimeOff = async (timeOffData) => {
  const response = await api.post("/admin-availability/time-off", timeOffData);
  return response.data;
};

const removeTimeOff = async (timeOffId) => {
  const response = await api.delete(
    `/admin-availability/time-off/${timeOffId}`
  );
  return response.data;
};

const addSpecialHours = async (specialHoursData) => {
  const response = await api.post(
    "/admin-availability/special-hours",
    specialHoursData
  );
  return response.data;
};

// ========================================
// APPOINTMENT SETTINGS
// ========================================

const updateAppointmentSettings = async (settingsData) => {
  const response = await api.put(
    "/admin-availability/appointment-settings",
    settingsData
  );
  return response.data;
};

const toggleAcceptingAppointments = async (acceptsAppointments) => {
  const response = await api.put("/admin-availability/toggle-appointments", {
    acceptsAppointments,
  });
  return response.data;
};

// ========================================
// SLOT MANAGEMENT
// ========================================

const getAvailableSlots = async (params) => {
  // params: { adminCode, date, duration }
  const response = await api.get("/admin-availability/slots", { params });
  return response.data;
};

const getAvailableSlotsRange = async (params) => {
  // params: { adminCode, startDate, endDate, duration }
  const response = await api.get("/admin-availability/slots/range", {
    params,
  });
  return response.data;
};

// ========================================
// NOTIFICATION SETTINGS
// ========================================

const updateNotificationSettings = async (notificationData) => {
  const response = await api.put(
    "/admin-availability/notifications",
    notificationData
  );
  return response.data;
};

// ========================================
// SEARCH & FILTER
// ========================================

const getAdminsBySpecialization = async (params) => {
  // params: { specialization, branchCode }
  const response = await api.get("/admin-availability/by-specialization", {
    params,
  });
  return response.data;
};

// ========================================
// INTEGRATION ENDPOINTS
// ========================================

const getAdminDetails = async (adminCode) => {
  const response = await api.get(`/admin-service/${adminCode}`);
  return response.data;
};

const checkAdminAvailability = async (adminCode, availabilityData) => {
  const response = await api.post(
    `/admin-service/${adminCode}/check-availability`,
    availabilityData
  );
  return response.data;
};

const getBranchAdmins = async (branchCode) => {
  const response = await api.get(`/admin-service/branch/${branchCode}`);
  return response.data;
};

const getAdminsByRole = async (params) => {
  // params: { branchCode, role }
  const response = await api.get("/admin-service/role/query", { params });
  return response.data;
};

const getBulkAdminAvailability = async (bulkData) => {
  // bulkData: { adminCodes: [], dateTime }
  const response = await api.post("/admin-service/bulk/availability", bulkData);
  return response.data;
};

const updateAdminRating = async (adminCode, ratingData) => {
  const response = await api.post(
    `/admin-service/${adminCode}/rating`,
    ratingData
  );
  return response.data;
};

const updateAppointmentStats = async (adminCode, statsData) => {
  const response = await api.post(
    `/admin-service/${adminCode}/stats`,
    statsData
  );
  return response.data;
};

// ========================================
// BRANCH ACCESS MANAGEMENT
// ========================================

/**
 * Get admin's branch access details
 * @param {string} adminCode - Admin code
 * @returns {Promise} - Branch access data
 */
const getAdminBranchAccess = async (adminCode) => {
  const response = await api.get(`/admin-branches/${adminCode}/branches`);
  return response.data;
};

/**
 * Add secondary branch access to an admin
 * @param {string} adminCode - Admin code
 * @param {Object} branchData - { branchCode, accessLevel, canTakeAppointments }
 * @returns {Promise} - Updated branch access
 */
const addBranchAccess = async (adminCode, branchData) => {
  const response = await api.post(
    `/admin-branches/${adminCode}/branches`,
    branchData
  );
  return response.data;
};

/**
 * Update branch access permissions
 * @param {string} adminCode - Admin code
 * @param {string} branchCode - Branch code
 * @param {Object} updateData - { accessLevel, canTakeAppointments, permissions }
 * @returns {Promise} - Updated branch access
 */
const updateBranchAccessPermissions = async (
  adminCode,
  branchCode,
  updateData
) => {
  const response = await api.put(
    `/admin-branches/${adminCode}/branches/${branchCode}`,
    updateData
  );
  return response.data;
};

/**
 * Remove secondary branch access from an admin
 * @param {string} adminCode - Admin code
 * @param {string} branchCode - Branch code
 * @returns {Promise} - Result
 */
const removeBranchAccess = async (adminCode, branchCode) => {
  const response = await api.delete(
    `/admin-branches/${adminCode}/branches/${branchCode}`
  );
  return response.data;
};

/**
 * Set admin's primary branch
 * @param {string} adminCode - Admin code
 * @param {string} branchCode - New primary branch code
 * @param {string} accessLevel - Access level for primary branch
 * @returns {Promise} - Updated admin
 */
const setAdminPrimaryBranch = async (
  adminCode,
  branchCode,
  accessLevel = "MANAGE"
) => {
  const response = await api.put(
    `/admin-branches/${adminCode}/primary-branch`,
    { branchCode, accessLevel }
  );
  return response.data;
};

/**
 * Get all admins for a specific branch
 * @param {string} branchCode - Branch code
 * @param {Object} params - { accessLevel, includeSecondary }
 * @returns {Promise} - List of admins with access to the branch
 */
const getAdminsByBranch = async (branchCode, params = {}) => {
  const response = await api.get(`/admin-branches/by-branch/${branchCode}`, {
    params,
  });
  return response.data;
};

/**
 * Bulk assign branch access to multiple admins
 * @param {string} branchCode - Branch code
 * @param {Object} bulkData - { adminCodes: [], accessLevel, canTakeAppointments }
 * @returns {Promise} - Result
 */
const bulkAssignBranchAccess = async (branchCode, bulkData) => {
  const response = await api.post(
    `/admin-branches/by-branch/${branchCode}/bulk`,
    bulkData
  );
  return response.data;
};

// ========================================
// EXPORTS
// ========================================

const adminService = {
  // Admin Management
  getAdmin,
  getMyDetails,
  createAdmin,
  deleteAdmin,
  updateAdminPassword,
  resetPassword,
  requestPasswordReset,
  fetchAdmins,
  toggleBlockAdmin,
  getForgotPasswordRequests,
  completeInitialSetup,
  updateAdminBranchAccess,

  // Status Management
  getAdminStatus,
  updateMyStatus,
  getAvailableAdmins,

  // Availability Management
  getMyAvailability,
  updateWorkingHours,
  addTimeOff,
  removeTimeOff,
  addSpecialHours,

  // Appointment Settings
  updateAppointmentSettings,
  toggleAcceptingAppointments,

  // Slot Management
  getAvailableSlots,
  getAvailableSlotsRange,

  // Notification Settings
  updateNotificationSettings,

  // Search & Filter
  getAdminsBySpecialization,

  // Integration
  getAdminDetails,
  checkAdminAvailability,
  getBranchAdmins,
  getAdminsByRole,
  getBulkAdminAvailability,
  updateAdminRating,
  updateAppointmentStats,

  // Branch Access Management
  getAdminBranchAccess,
  addBranchAccess,
  updateBranchAccessPermissions,
  removeBranchAccess,
  setAdminPrimaryBranch,
  getAdminsByBranch,
  bulkAssignBranchAccess,
};

export default adminService;
